import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Users2, SlidersHorizontal, X, Loader2, Settings2 } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { EmptyState } from "@/components/common/Loading";
import { useReferences, labelFor } from "@/hooks/useReferences";
import { useAuth } from "@/context/AuthContext";
import { useLocalMode } from "@/context/LocalModeContext";
import { DirectoryFilters, DIR_FILTERS } from "@/components/directory/DirectoryFilters";
import { isRegionCenter } from "@/components/directory/GeoRadiusFilter";
import { MultiSelect } from "@/components/directory/MultiSelect";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";
import useGroupsConfig from "@/hooks/useGroupsConfig";
import { GroupCard, MODE_LABEL } from "@/components/groups/GroupCard";
import { CreateGroupDialog } from "@/components/groups/CreateGroupDialog";

const MODE_OPTIONS = Object.entries(MODE_LABEL).map(([value, label]) => ({ value, label }));
const emptyState = () => ({ multi: {}, sort: "recent", geoOn: false, center: null,
  radius: 50, online: false });

const SkeletonGrid = () => (
  <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
    {Array.from({ length: 6 }).map((_, i) => (
      <Card key={i} className="p-5">
        <div className="flex items-center gap-3">
          <Skeleton className="h-12 w-12 rounded-xl" />
          <div className="flex-1 space-y-2"><Skeleton className="h-4 w-2/3" /><Skeleton className="h-3 w-1/3" /></div>
        </div>
        <Skeleton className="mt-4 h-3 w-full" />
        <Skeleton className="mt-2 h-3 w-4/5" />
      </Card>
    ))}
  </div>
);

/**
 * `/groups` — PUBLIC Group discovery. The ONE canonical Group directory and a primary
 * Startup Otter destination (GRP-DEC-027), reachable without an account (GRP-DEC-289).
 *
 * Deliberately built on the SAME architecture as /people, /businesses and /capital:
 * `DirectoryFilters` (shared MultiSelects + the shared geo-radius/region control),
 * the shared reference data (including Business `business_classifications`), applied
 * filter chips, the shared sort options, `useInfiniteScroll` paging and Local Mode
 * parameters. Only genuinely Group-specific filters (membership type, online) are new.
 * Personal management lives in the Dashboard at `/dashboard/groups`.
 */
export default function Groups() {
  const { visible, loading } = useGroupsConfig();
  const { references } = useReferences();
  const { user } = useAuth();
  const localMode = useLocalMode();
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");
  const [filters, setFilters] = useState(emptyState);
  const [creating, setCreating] = useState(false);
  const groups = useMemo(() => DIR_FILTERS.groups, []);

  useEffect(() => { const t = setTimeout(() => setDq(q.trim()), 300); return () => clearTimeout(t); }, [q]);

  // Default the distance center to the signed-in member's saved location, exactly like
  // the other directories do.
  useEffect(() => {
    let ok = true;
    if (user) {
      api.get("/directory/my-location").then(({ data }) => {
        if (ok && data && data.lat != null) {
          setFilters((f) => (f.center ? f : { ...f, center: {
            lat: data.lat, lng: data.lng, label: data.label || "My location" } }));
        }
      }).catch(() => {});
    }
    return () => { ok = false; };
  }, [user]);

  const patch = useCallback((p) => setFilters((f) => ({ ...f, ...p })), []);
  const clearAll = useCallback(() => setFilters((f) => ({
    ...emptyState(), center: f.center })), []);

  useEffect(() => {
    if (!filters.center && filters.sort === "nearest") setFilters((f) => ({ ...f, sort: "recent" }));
  }, [filters.center, filters.sort]);

  const multiKey = JSON.stringify(filters.multi);
  const centerKey = filters.center ? `${filters.center.lat},${filters.center.lng}` : "";

  const fetchPage = useCallback(async (offset, signal) => {
    const params = { limit: 24, offset };
    if (dq) params.q = dq;
    groups.forEach((g) => {
      const arr = filters.multi[g.key];
      if (arr && arr.length) params[g.key] = arr.join(",");
    });
    const modes = filters.multi.membership_modes;
    if (modes && modes.length) params.membership_modes = modes.join(",");
    if (filters.online) params.online = true;
    let sort = filters.sort;
    if (filters.geoOn && filters.center) {
      const c = filters.center;
      if (isRegionCenter(c)) {
        if (c.granularity === "state") params.state = c.state_code;
        else params.country = c.country_code;
      } else {
        params.lat = c.lat; params.lng = c.lng; params.radius = filters.radius;
      }
    } else if (sort === "nearest") {
      sort = "recent";
    }
    params.sort = sort;
    if (localMode.localParams) Object.assign(params, localMode.localParams);
    const { data } = await api.get("/groups/discover", { params, signal });
    return data.groups || [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dq, multiKey, filters.sort, filters.geoOn, filters.radius, filters.online, centerKey,
    localMode.localKey]);

  const resetKey = `groups|${dq}|${multiKey}|${filters.sort}|${filters.geoOn}|${filters.radius}|${centerKey}|${filters.online}|${localMode.localKey}`;
  const { items, loadingMore, hasMore, sentinelRef } = useInfiniteScroll({
    fetchPage, resetKey, pageSize: 24 });

  const chips = useMemo(() => {
    const out = [];
    groups.forEach((g) => (filters.multi[g.key] || []).forEach((v) =>
      out.push({ key: g.key, value: v, label: labelFor(references[g.ref], v) })));
    (filters.multi.membership_modes || []).forEach((v) =>
      out.push({ key: "membership_modes", value: v, label: MODE_LABEL[v] || v }));
    if (filters.online) out.push({ key: "__online", value: "online", label: "Online groups" });
    if (filters.geoOn && filters.center) {
      out.push({ key: "__geo", value: "geo",
        label: isRegionCenter(filters.center) ? `In ${filters.center.label}`
          : `Within ${filters.radius} mi of ${filters.center.label}` });
    }
    return out;
  }, [filters, groups, references]);

  const removeChip = (chip) => {
    if (chip.key === "__geo") return patch({ geoOn: false });
    if (chip.key === "__online") return patch({ online: false });
    return patch({ multi: { ...filters.multi,
      [chip.key]: (filters.multi[chip.key] || []).filter((v) => v !== chip.value) } });
  };

  const extraFilters = (
    <>
      <div>
        <Label className="mb-1.5 block text-xs font-[600] text-muted-foreground">Membership</Label>
        <MultiSelect label="Any membership" options={MODE_OPTIONS}
                     selected={filters.multi.membership_modes || []}
                     onChange={(arr) => patch({ multi: { ...filters.multi, membership_modes: arr } })}
                     testId="filter-membership_modes" />
      </div>
      <div className="flex items-center justify-between">
        <Label htmlFor="groups-online-switch" className="text-sm font-[560]">Online groups</Label>
        <Switch id="groups-online-switch" checked={filters.online}
                onCheckedChange={(v) => patch({ online: v })}
                data-testid="groups-filter-online" />
      </div>
    </>
  );

  const filterPanel = (
    <DirectoryFilters kind="groups" value={filters} onChange={patch} onClear={clearAll}
                      hasActive={chips.length > 0} extra={extraFilters} />
  );

  if (loading) return <div className="mx-auto max-w-7xl px-4 py-10"><SkeletonGrid /></div>;
  if (!visible) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 text-center" data-testid="groups-unavailable">
        <Users2 className="mx-auto h-8 w-8 text-muted-foreground" />
        <p className="mt-3 text-base font-semibold">Groups aren&apos;t available</p>
        <p className="mt-1 text-sm text-muted-foreground">This area isn&apos;t enabled yet.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid="groups-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-[680] tracking-[-0.01em] text-foreground">Groups</h1>
          <p className="mt-2 text-muted-foreground">
            Discover communities of founders, operators and investors.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {user ? (
            <Button asChild variant="secondary" data-testid="groups-manage-link">
              <Link to="/dashboard/groups"><Settings2 className="mr-2 h-4 w-4" />My groups</Link>
            </Button>
          ) : null}
          {user ? (
            <Button onClick={() => setCreating(true)} data-testid="groups-create-button">
              Create group
            </Button>
          ) : (
            <Button asChild data-testid="groups-create-signin">
              <Link to="/login?next=/groups">Create group</Link>
            </Button>
          )}
        </div>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-12">
        <aside className="hidden lg:col-span-3 lg:block">
          <div className="lg:sticky lg:top-20">{filterPanel}</div>
        </aside>

        <div className="lg:col-span-9">
          <div className="flex flex-wrap items-center gap-2">
            <div className="min-w-[200px] flex-1">
              <Input placeholder="Search groups…" value={q} onChange={(e) => setQ(e.target.value)}
                     data-testid="groups-search-input" />
            </div>
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="secondary" className="lg:hidden" data-testid="groups-mobile-filters">
                  <SlidersHorizontal className="mr-1.5 h-4 w-4" /> Filters
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[320px] overflow-y-auto">{filterPanel}</SheetContent>
            </Sheet>
            <Select value={filters.sort} onValueChange={(v) => patch({ sort: v })}>
              <SelectTrigger className="w-[170px]" data-testid="groups-sort"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="name">Name (A–Z)</SelectItem>
                <SelectItem value="name_desc">Name (Z–A)</SelectItem>
                <SelectItem value="recent">Newest Added</SelectItem>
                <SelectItem value="nearest" disabled={!filters.center}>Nearest</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {chips.length > 0 && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {chips.map((c, i) => (
                <button key={`${c.key}-${c.value}-${i}`} onClick={() => removeChip(c)}
                        className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-3 py-1 text-xs font-[500] text-foreground transition-colors hover:bg-secondary"
                        data-testid={`applied-chip-${c.value}`}>
                  {c.label}<X className="h-3 w-3" />
                </button>
              ))}
              <button onClick={clearAll} className="text-xs font-[540] text-primary hover:underline"
                      data-testid="groups-filters-clear">Clear all</button>
            </div>
          )}

          {items !== null && (
            <div className="mt-4 text-sm text-muted-foreground" data-testid="groups-result-count">
              {items.length} result{items.length !== 1 ? "s" : ""}
            </div>
          )}

          {items === null ? <SkeletonGrid /> : items.length === 0 ? (
            <div className="mt-4">
              <EmptyState icon={Users2} title="No groups match"
                          description="Try removing a filter or widening your distance."
                          action={<button onClick={clearAll} className="text-primary hover:underline">Clear all filters</button>}
                          testId="groups-empty" />
            </div>
          ) : (
            <>
              <div className="mt-2 grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3"
                   data-testid="groups-discover-list">
                {items.map((g) => <GroupCard key={g.id} group={g} />)}
              </div>
              <div ref={sentinelRef} aria-hidden="true" className="h-1" />
              {loadingMore && (
                <div className="mt-6 flex justify-center" data-testid="groups-loading-more">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              )}
              {!hasMore && items.length > 0 && (
                <div className="mt-6 text-center text-sm text-muted-foreground" data-testid="groups-end">
                  You&apos;ve reached the end · {items.length} result{items.length !== 1 ? "s" : ""}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <CreateGroupDialog open={creating} onOpenChange={setCreating} onCreated={() => setQ("")} />
    </div>
  );
}
